import { type Variants } from "framer-motion"

export type TransitionSize = "sm" | "md" | "lg"
export type TransitionCustom = { reverse?: boolean; size?: TransitionSize; delay?: number }

const DEFAULT_SIZE: TransitionSize = "sm"
const DISTANCE_BASE = 30
const DURATION_BASE = 0.25
export const DURATION_SHORT = getDuration("sm")
export const DURATION_MEDIUM = getDuration("md")
export const DURATION_LONG = getDuration("lg")

function getMultiplier(size: TransitionSize): number {
  switch (size) {
    case "sm":
      return 1
    case "md":
      return 2
    case "lg":
      return 3
  }
}
export function getDuration(size: TransitionSize): number {
  return DURATION_BASE * getMultiplier(size)
}

export function getTransition(multiplier: number, delay?: number) {
  return { duration: DURATION_BASE * multiplier, delay: delay || 0 }
}

function slideInitial(custom?: TransitionCustom) {
  const direction = custom?.reverse ? -1 : 1
  const multiplier = getMultiplier(custom?.size || DEFAULT_SIZE)
  return {
    x: DISTANCE_BASE * direction * multiplier,
    opacity: 0,
    transition: getTransition(multiplier, custom?.delay),
  }
}

function slideExit(custom?: TransitionCustom) {
  return slideInitial({
    ...custom,
    reverse: !custom?.reverse,
  })
}

function slideAnimate(custom?: TransitionCustom) {
  const multiplier = getMultiplier(custom?.size || DEFAULT_SIZE)
  return {
    x: 0,
    opacity: 1,
    transition: {
      ...getTransition(multiplier, custom?.delay),
      type: "spring" as const,
      stiffness: 225 + 25 * multiplier,
      damping: 22 + multiplier,
    },
  }
}

export const slide: Variants = {
  initial: slideInitial,
  animate: slideAnimate,
  exit: slideExit,
}

export const fade: Variants = {
  initial: (custom?: { size: TransitionSize }) => ({
    opacity: 0,
    transition: getTransition(getMultiplier(custom?.size || "lg")),
  }),
  animate: (custom?: { size: TransitionSize }) => ({
    opacity: 1,
    transition: getTransition(getMultiplier(custom?.size || "lg")),
  }),
  exit: (custom?: { size: TransitionSize }) => ({
    opacity: 0,
    transition: getTransition(getMultiplier(custom?.size || "lg")),
  }),
}
